import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import authApi from '../../api/authApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { format, getUnixTime, startOfDay } from 'date-fns';
import { Link } from 'react-router-dom';
import TopOrdersPodium from './top-orders-podium';
import TopOrdersPrizes from './top-orders-prizes';
import Top4Orders from './Top4Orders';

const TODAY = new Date();
const YEAR = TODAY.getFullYear();
const MONTH = TODAY.getMonth();

const makeTimeRange = (monthOffset) => ({
    startTime: getUnixTime(startOfDay(new Date(YEAR, MONTH + monthOffset, 1))),
    endTime: getUnixTime(startOfDay(new Date(YEAR, MONTH + monthOffset + 1, 1))),
});

function OrdersModal({ user, onClose }) {
    if (!user) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-surface rounded-2xl p-5 w-full max-w-sm shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-base font-bold">Chi tiết đơn hàng</h3>
                    <button onClick={onClose} className="p-1.5 hover:bg-surface-muted rounded-full transition-colors">
                        <FontAwesomeIcon icon={faTimes} className="text-content-3" />
                    </button>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between items-center p-3 bg-surface-sunken rounded-xl">
                        <span className="text-sm font-semibold">Tổng đơn hàng</span>
                        <span className="font-bold text-primary-600">{user.totalOrders || 0} đơn</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                        <span className="text-sm text-green-700">Đã duyệt</span>
                        <span className="font-semibold text-green-600">{user.confirmedOrders || 0} đơn</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-orange-50 rounded-xl">
                        <span className="text-sm text-orange-700">Chờ duyệt</span>
                        <span className="font-semibold text-orange-500">{user.pendingOrders || 0} đơn</span>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="mt-4 w-full bg-primary-600 text-white py-2.5 rounded-xl font-semibold hover:bg-primary-700 transition-colors"
                >
                    Đóng
                </button>
            </div>
        </div>
    );
}

function TopOrdersV2() {
    const [users, setUsers] = useState([]);
    const [isThisMonth, setIsThisMonth] = useState(true);
    const [time, setTime] = useState(makeTimeRange(0));
    const [selectedUser, setSelectedUser] = useState(null);

    const displayMonth = isThisMonth ? MONTH + 1 : MONTH === 0 ? 12 : MONTH;
    const displayYear = isThisMonth ? YEAR : MONTH === 0 ? YEAR - 1 : YEAR;

    const handleTimeChange = (thisMonth) => {
        if (thisMonth === isThisMonth) return;
        setIsThisMonth(thisMonth);
        setTime(makeTimeRange(thisMonth ? 0 : -1));
    };

    useEffect(() => {
        const fetchUsers = async () => {
            const ONLY_NUM_REGEX = /^\d+$/;
            try {
                const { data } = await authApi.topOrdersOfMonth(time.startTime, time.endTime);
                const valid = data.userList.filter(
                    (el) => el.userId && el.userId !== '' && ONLY_NUM_REGEX.test(el.userId)
                );
                const list = valid.slice(0, 20);
                const fill = new Array(Math.max(0, 20 - list.length)).fill({
                    userId: '-',
                    totalOrders: 0,
                    confirmedOrders: 0,
                    pendingOrders: 0,
                });
                setUsers([...list, ...fill]);
            } catch (e) {
                console.log(e);
            }
        };
        fetchUsers();
    }, [time]);

    return (
        <div className="min-h-screen bg-surface-sunken">
            <Header hasBackButton redirectPath="/" />

            {/* Hero banner */}
            <div className="pt-16 bg-gradient-to-b from-blue-700 via-blue-500 to-indigo-400">
                <div className="text-center pt-5 pb-3 text-white md:w-[768px] lg:w-[1024px] mx-auto">
                    <p className="text-xs font-medium uppercase tracking-widest opacity-80">Sự kiện hàng tháng</p>
                    <h1 className="font-lexend font-extrabold text-2xl mt-1 drop-shadow">ĐUA TOP ĐƠN HÀNG</h1>
                    <p className="text-sm font-medium mt-1 opacity-90">Tháng {displayMonth}/{displayYear}</p>

                    {/* Time filter tabs */}
                    <div className="flex justify-center gap-2 mt-3">
                        <button
                            onClick={() => handleTimeChange(false)}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                                !isThisMonth ? 'bg-surface text-blue-600' : 'bg-surface/20 text-white'
                            }`}
                        >
                            Tháng trước
                        </button>
                        <button
                            onClick={() => handleTimeChange(true)}
                            className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                                isThisMonth ? 'bg-surface text-blue-600' : 'bg-surface/20 text-white'
                            }`}
                        >
                            Tháng này
                        </button>
                    </div>
                </div>

                {/* Podium top 3 */}
                <div className="md:w-[768px] lg:w-[1024px] mx-auto">
                    {users.length > 0 && (
                        <TopOrdersPodium users={users} onUserClick={setSelectedUser} />
                    )}
                </div>
            </div>

            {/* White content card */}
            <div className="bg-surface rounded-t-3xl -mt-3 shadow-lg md:w-[768px] lg:w-[1024px] mx-auto">
                <TopOrdersPrizes />
                <Top4Orders users={users.slice(3)} onOrdersClick={setSelectedUser} />
                <div className="text-center text-xs text-content-3 pb-10 px-4 space-y-1 pt-2">
                    <p>Cập nhật lần cuối: {format(new Date(), 'HH:mm dd/MM/yyyy')}</p>
                    <p>
                        Xem Quy định tham gia{' '}
                        <Link to="/post/21" className="font-semibold text-primary-600 underline">
                            TẠI ĐÂY
                        </Link>
                    </p>
                </div>
            </div>

            <OrdersModal user={selectedUser} onClose={() => setSelectedUser(null)} />
        </div>
    );
}

export default TopOrdersV2;
